import _styled, { default as _styled2 } from "styled-components";
var _templateObject;
const _excluded = ["children", "className", "title", "onClose", "onAnimationStart", "onAnimationComplete", "renderHeader", "renderSubHeader", "renderFooter", "open", "style", "width", "floating", "disableAnimation", "shouldCloseOnEsc"];
function _taggedTemplateLiteral(strings, raw) { if (!raw) { raw = strings.slice(0); } return Object.freeze(Object.defineProperties(strings, { raw: { value: Object.freeze(raw) } })); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } } return target; }
import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Animation from '@adsk/alloy-react-animation';
import Divider from '@adsk/alloy-react-divider';
import { useKeyEvent } from '@adsk/alloy-react-helpers';
import theme, { stylePropType } from '@adsk/alloy-react-theme';
import { WIDTHS } from './consts';
import { Container, Header, Body } from './layout';
const DIVIDER_STYLE = {
  marginLeft: theme.spacing.M,
  width: 'calc(100% - 16px - 16px)'
};
const ESCAPE = 'Escape';
/**
 * A Panel component.  Provides access to secondary content which only appears when specifically requested by a user.
 */
const Panel = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      children,
      className,
      title,
      onClose,
      onAnimationStart,
      onAnimationComplete,
      renderHeader = p => /*#__PURE__*/React.createElement(Header, p),
      renderSubHeader,
      renderFooter,
      open = false,
      style,
      width = WIDTHS.MEDIUM,
      floating = false,
      disableAnimation = false,
      shouldCloseOnEsc = true
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const innerRef = useRef(null);
  const [isFullyOpen, setIsFullyOpen] = useState(open);
  const onEscKeyDown = useCallback(e => {
    if (e.key === ESCAPE) {
      shouldCloseOnEsc && (onClose === null || onClose === void 0 ? void 0 : onClose());
    }
  }, [shouldCloseOnEsc, onClose]);
  useKeyEvent(ESCAPE, {
    onKeyDown: onEscKeyDown
  });
  const handleOnAnimationComplete = x => {
    setIsFullyOpen(open);
    onAnimationComplete && onAnimationComplete(x);
  };
  const motionProps = {
    width: open ? width : 0
  };
  const inlineStyle = _objectSpread(_objectSpread({
    height: '100%',
    overflow: 'hidden',
    backgroundColor: theme.tokens.colors.background.primary.value,
    flexShrink: 0,
    flexWrap: 'wrap',
    display: 'flex',
    flexDirection: 'column',
    top: 0,
    width: disableAnimation ? 0 : undefined
  }, floating && {
    position: 'absolute',
    zIndex: theme.zIndex.panel,
    right: 0
  }), open && {
    boxShadow: floating ? theme.shadows.highElevation : "0 0 0 1px ".concat(theme.tokens.colors.border.subtle.value),
    width: disableAnimation ? width : undefined
  });
  const renderContents = () => /*#__PURE__*/React.createElement(_StyledDiv, {
    ref: innerRef,
    $_css: [{
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      height: '100%'
    }, _objectSpread({}, isFullyOpen && open && {
      maxWidth: '100%'
    })]
  }, renderHeader({
    title,
    onClose,
    showEscapeLabel: shouldCloseOnEsc
  }), renderSubHeader === null || renderSubHeader === void 0 ? void 0 : renderSubHeader({
    style: {
      padding: "".concat(theme.spacing.XXS + 2, "px ").concat(theme.spacing.M, "px")
    }
  }), !!renderSubHeader && /*#__PURE__*/React.createElement(Divider, {
    style: DIVIDER_STYLE
  }), children, !!renderFooter && /*#__PURE__*/React.createElement(Divider, {
    style: DIVIDER_STYLE
  }), renderFooter === null || renderFooter === void 0 ? void 0 : renderFooter({
    style: {
      padding: "".concat(theme.spacing.S, "px ").concat(theme.spacing.M, "px ").concat(theme.spacing.M, "px")
    },
    onClose
  }));
  return disableAnimation ? /*#__PURE__*/React.createElement(_StyledDiv2, _extends({}, props, {
    ref: ref,
    className: className,
    $_css2: [inlineStyle, style]
  }), renderContents()) : /*#__PURE__*/React.createElement(Animation, _extends({}, props, {
    ref: ref,
    className: className,
    style: [inlineStyle, style],
    initial: motionProps,
    animate: motionProps,
    transition: {
      ease: 'easeOut',
      duration: 0.3
    },
    onAnimationStart: onAnimationStart,
    onAnimationComplete: handleOnAnimationComplete
  }), renderContents());
});
Panel.displayName = 'Panel';
Panel.propTypes = {
  /** If panel is open or not */
  open: PropTypes.bool,
  /** Panel content */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Render custom header content. Props: ({ title, onClose}) */
  renderHeader: PropTypes.func,
  /** Render custom sub header content */
  renderSubHeader: PropTypes.func,
  /** Render custom footer content */
  renderFooter: PropTypes.func,
  /** Width of Panel.  Use Panel.WIDTHS for standard widths */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** If panel should overlay content.  Default false for small/medium, true for large */
  floating: PropTypes.bool,
  /** Title to display in header */
  title: PropTypes.any,
  /** Function executed upon clicking close button */
  onClose: PropTypes.func,
  /** Function executed upon end of animation */
  onAnimationComplete: PropTypes.func,
  /** Disable panel animations */
  disableAnimation: PropTypes.bool,
  /** Set whether the panel should be closed when pressing the ESC key */
  shouldCloseOnEsc: PropTypes.bool
};
export default Object.assign(Panel, {
  WIDTHS,
  Container,
  Header,
  Body
});
var _StyledDiv = _styled("div")(_templateObject || (_templateObject = _taggedTemplateLiteral(["", ""])), p => p.$_css);
var _StyledDiv2 = _styled2("div").withConfig({
  displayName: "Panel___StyledDiv2",
  componentId: "sc-tfulkf-0"
})(["", ""], p => p.$_css2);
//# sourceMappingURL=Panel.js.map